import { fontByKey } from './editorCatalog';

export const TEXT_SIZE = 57;
const measured = new Map();
const listeners = new Set();
export const onMeasured = fn => { listeners.add(fn); return () => listeners.delete(fn); };
export const setMeasuredText = (id, box) => {
  const old = measured.get(id); measured.set(id, box);
  if (!old || Math.abs(old.w - box.w) > .5 || Math.abs(old.h - box.h) > .5) listeners.forEach(fn => fn());
};
export const measuredText = o => measured.get(o.id) || { w: Math.max(40, (o.text || ' ').length * TEXT_SIZE * .52), h: TEXT_SIZE * 1.25 };

let context;
export function advanceOf(text, fontKey) {
  context ||= document.createElement('canvas').getContext('2d');
  const font = fontByKey(fontKey);
  context.font = `${font.style === 'italic' ? 'italic ' : ''}${font.weight} ${TEXT_SIZE}px "${font.css}", "Cormorant Garamond", serif`;
  return Math.max(1, context.measureText(text || ' ').width);
}

// Actual glyph ink extents (not the em box) around a middle-anchored baseline at 0/0.
export function inkMetrics(text, fontKey) {
  advanceOf(text, fontKey);
  context.textAlign = 'center';
  const m = context.measureText(text || ' ');
  context.textAlign = 'start';
  return { left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight, ascent: m.actualBoundingBoxAscent, descent: m.actualBoundingBoxDescent, fontAscent: m.fontBoundingBoxAscent ?? TEXT_SIZE * .9, fontDescent: m.fontBoundingBoxDescent ?? TEXT_SIZE * .25 };
}

// Ink extents of one left-anchored line at baseline 0 (letter spacing extends the right edge).
export function inkLine(text, fontKey, spacing = 0) {
  advanceOf(text, fontKey);
  const m = context.measureText(text || ' ');
  return { left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight + spacing * Math.max(0, text.length - 1), ascent: m.actualBoundingBoxAscent, descent: m.actualBoundingBoxDescent, advance: m.width + spacing * Math.max(0, text.length - 1) };
}

// Same line placement as the backend text engine: block width = widest line, baselines every lineHeight × size.
export function textLayout(lines, fontKey, spacing, lineHeight, align) {
  const metrics = lines.map(l => inkLine(l, fontKey, spacing)), width = Math.max(...metrics.map(m => m.advance));
  let box = null;
  const runs = metrics.map((m, i) => {
    const x = align === 'left' ? 0 : align === 'right' ? width - m.advance : (width - m.advance) / 2, y = i * lineHeight * TEXT_SIZE;
    if (lines[i].trim()) { const b = { x1: x - m.left, x2: x + m.right, y1: y - m.ascent, y2: y + m.descent }; box = box ? { x1: Math.min(box.x1, b.x1), x2: Math.max(box.x2, b.x2), y1: Math.min(box.y1, b.y1), y2: Math.max(box.y2, b.y2) } : b; }
    return { x, y };
  });
  box ||= { x1: 0, x2: 1, y1: -TEXT_SIZE * .7, y2: 0 };
  return { runs, box: { x: box.x1, y: box.y1, w: box.x2 - box.x1, h: box.y2 - box.y1 } };
}

// Same circular geometry as the backend text engine: total angle = |curve|°, radius = advance / angle.
export function textPath(advance, curve) {
  if (Math.abs(curve) < .01) return `M${-advance / 2} 0 H${advance / 2}`;
  const theta = Math.abs(curve) * Math.PI / 180, r = advance / theta, dir = curve > 0 ? 1 : -1;
  const x = r * Math.sin(theta / 2), y = dir * r * (1 - Math.cos(theta / 2));
  return `M${-x} ${y} A${r} ${r} 0 0 ${dir > 0 ? 1 : 0} ${x} ${y}`;
}
